<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { useSectionLinesStore } from '@/stores/sectionLine'
  import type { SectionLineUploadCollection } from '@/stores/type/sectionLine.type'
  import { useSectionLineUploadCollectionsStore } from '@/stores/sectionLineUploadCollections'
  import { useEventsBus } from '@haskoning/vue-component-library'

  const { emit: busEmit } = useEventsBus()

  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()

  const { items: sectionLineCollections, selectedItems: selectedSectionLineCollections } =
    storeToRefs(sectionLineUploadCollectionsStore)

  const { updateSectionLineDialog, currentItem, writeLoading, newSectionLineCoordinates } =
    storeToRefs(sectionLinesStore)

  const localName = ref('')

  const updateCurrentItemProperties = () => {
    if (currentItem.value?.properties) {
      currentItem.value.properties.section_title = localName.value
    }
  }

  const handleSave = async () => {
    const isEditMode = updateSectionLineDialog.value.selectedMode === 'edit'
    if (isEditMode) {
      updateCurrentItemProperties()
      await updateSectionLine()
    } else {
      await createSectionLine()
    }
    localName.value = ''
  }

  const createSectionLine = async () => {
    const createObj = {
      section_line_upload_collection: selectedSectionLineCollections?.value[0]?.id,
      sectionlineuploadcollection: selectedSectionLineCollections?.value[0]?.id,
      section_title: localName.value,
      linestring_geom: newSectionLineCoordinates.value
    }
    try {
      const responseData = await sectionLinesStore.create(createObj)
      const sectionLineCollection = sectionLineCollections.value.find(
        (item: SectionLineUploadCollection) =>
          item.id === selectedSectionLineCollections?.value[0]?.id
      )
      sectionLineCollection?.sectionline_set.features.push(responseData)
      sectionLinesStore.toggleUpdateSectionLineDialog('create')
      sectionLinesStore.setSectionLineCoordinates('')
    } catch (error) {
      console.error('Error creating section line:', error)
    }
  }

  const updateSectionLine = async () => {
    try {
      await sectionLinesStore.update(currentItem.value)
      sectionLinesStore.toggleUpdateSectionLineDialog()
    } catch (error) {
      console.error('Error updating section line:', error)
    }
  }

  const toggleDrawEvent = () => {
    busEmit('draw-section-line')
    sectionLinesStore.toggleUpdateSectionLineDialog()
  }

  watch(currentItem, (newItem) => {
    if (newItem?.properties && updateSectionLineDialog.value.selectedMode === 'edit') {
      localName.value = newItem.properties.section_title
    }
  })

  watch(updateSectionLineDialog.value, (newVal) => {
    if (newVal.selectedMode === 'create') {
      localName.value = ''
    } else localName.value = currentItem.value?.properties?.section_title
  })
</script>

<template>
  <v-dialog width="400" v-model="updateSectionLineDialog.show">
    <v-card>
      <v-card-title>
        {{
          updateSectionLineDialog.selectedMode === 'edit'
            ? 'Edit Section Line'
            : 'Create a new Section Line'
        }}
      </v-card-title>
      <v-card-text>
        <v-text-field
          label="Section Line Title"
          v-model="localName"
          color="primary"
          :clearable="true"
        ></v-text-field>
        <v-text-field
          v-if="updateSectionLineDialog.selectedMode === 'create'"
          :readonly="true"
          label="Coordinates"
          v-model="newSectionLineCoordinates.coordinates"
          color="primary"
        >
          <template v-slot:prepend>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <v-icon
                  @click="toggleDrawEvent"
                  v-ripple="true"
                  v-bind="props"
                  icon="mdi-draw-pen"
                ></v-icon>
              </template>
              Edit the section line coordinates
            </v-tooltip>
          </template>
        </v-text-field>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="sectionLinesStore.toggleUpdateSectionLineDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          :disabled="!localName"
          :loading="writeLoading"
          color="primary"
          variant="text"
          @click="handleSave"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
